import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import type { SystemRoleCode } from '@libs/dtos/role/role.dto';
import { DateService } from '@services/shared/date.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { TableAction, ToggleActionEvent } from '@shared/components/table/table.model';
import { TagComponent } from '@shared/components/tag/tag.component';
import { SharedModule } from '@shared/modules/shared.module';
import { ActionMenuComponent } from '../table/action-menu/action-menu.component';

@Component({
	selector: 'app-card',
	imports: [SharedModule, ButtonComponent, TagComponent],
	templateUrl: './card.component.html',
	styleUrl: './card.component.scss',
})
export class CardComponent {
	// ====== Public variables ======
	active = false;
	actions: TableAction[] = [];

	// ====== Input Properties ======
	@Input() item: any = {};
	@Input() getMenuActions?: (data: any) => TableAction[];
	@Input() menuActions: TableAction[] = [];
	/** System role of the viewer: the pending-response tags read from its side (client or staff) */
	@Input() userRole?: SystemRoleCode;

	// ====== Output Events ======
	@Output() toggleActionMenu = new EventEmitter<ToggleActionEvent>();
	@Output() onMenuAction = new EventEmitter<{
		action: string;
		data: unknown;
	}>();
	@Output() onView = new EventEmitter<any>();

	constructor(
		private dateService: DateService,
		private overlay: Overlay,
		private positionBuilder: OverlayPositionBuilder,
	) {}

	/**
	 * Handles the toggling of the action menu
	 * When activated, loads the menu actions if getMenuActions is provided
	 */
	handleToggleActionMenu(): void {
		this.active = !this.active;

		if (this.active && this.getMenuActions) {
			this.actions = this.getMenuActions(this.item);
		}
	}

	/**
	 * Emits the view event with the current item data
	 * Used to trigger navigation to item details
	 */
	onViewClick() {
		this.onView.emit(this.item);
	}

	getTimeAgo(): string {
		// Check if we have a valid ISO date
		if (this.item.createdAt && typeof this.item.createdAt === 'string' && this.item.createdAt.includes('T')) {
			return this.dateService.timeAgoShort(this.item.createdAt);
		}

		// If it's not an ISO date, return the text as-is
		return this.item.createdAt || '';
	}

	private overlayRef: OverlayRef | null = null;

	openActionMenu(origin: EventTarget | null, actions: any[], rowData: any) {
		if (!origin || !(origin instanceof HTMLElement)) {
			console.error('Could not get button element for the overlay');
			return;
		}
		// Close the previous overlay if it exists
		if (this.overlayRef) {
			this.overlayRef.dispose();
		}

		// Create the position strategy
		const positionStrategy = this.positionBuilder.flexibleConnectedTo(origin).withPositions([
			{
				originX: 'end',
				originY: 'bottom',
				overlayX: 'end',
				overlayY: 'top',
				offsetY: 8,
			},
			{
				originX: 'end',
				originY: 'top',
				overlayX: 'end',
				overlayY: 'bottom',
				offsetY: -8,
			},
		]);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			hasBackdrop: true,
			backdropClass: 'cdk-overlay-transparent-backdrop',
			scrollStrategy: this.overlay.scrollStrategies.reposition(),
		});

		const portal = new ComponentPortal(ActionMenuComponent);
		const componentRef = this.overlayRef.attach(portal);

		(componentRef.instance as ActionMenuComponent).actions = actions;
		(componentRef.instance as ActionMenuComponent).rowData = rowData;

		this.overlayRef.backdropClick().subscribe(() => this.overlayRef?.dispose());
	}
}
